import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import LockIcon from "@mui/icons-material/Lock";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import {
  Box,
  Button,
  InputLabel,
  OutlinedInput,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";

import Title from "../../common/Title";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useAlert from "../../../hooks/useAlert";
import { __getUserList } from "../../../api/user";
import InputFieldBox from "../../common/InputFieldBox";
import { __getGroups, __updateUser } from "../../../api/group";
import InputFieldAutoComplete from "../../common/InputFieldAutoComplete";
import { can_update_user_api, generatePassword } from "../../../utils/data";

const UpdateUser = () => {
  const { user } = useAuth();
  const nav = useNavigate();

  if (user.sub_user) {
    nav("/mdn");
  }

  const [userResponse, userCall, userApiLoading] = useAxios(__getUserList);
  const [groupResponse, groupCall, groupApiLoading] = useAxios(__getGroups);

  const { setAlertMessage } = useAlert();
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__updateUser,
  });
  const initialData = {
    password: "",
    allow_reset: true,
    britex_customer_id: [],
    sip_enabled: false,
  };

  const validationSchema = yup.object({
    password: yup.string("password").optional().min(4),
    forward_url: yup.string("forward_url").optional(),
    forward_url2: yup.string("forward_url2").optional(),
    api_key: yup
      .string("api_key")
      .nullable()
      .max(50, "Cannot be more than 50 characters long.")
      .optional(),
    allow_reset: yup.boolean("allow_reset").optional(),
    sip_enabled: yup.boolean("sip_enabled").optional(),
    britex_customer_id: yup.array().of(yup.number()),
  });

  useEffect(() => {
    if (currentUser) {
      const filterUser = users.find((user) => user.id === currentUser.id);
      formik.setFieldValue("forward_url", filterUser.forward_url || "");
      formik.setFieldValue("forward_url2", filterUser.forward_url2 || "");
      formik.setFieldValue("allow_reset", filterUser.allow_reset);
      formik.setFieldValue("sip_enabled", !!filterUser.sip_enabled);
      formik.setFieldValue(
        "britex_customer_id",
        filterUser.britex_customer_id || []
      );
      can_update_user_api(user) &&
        formik.setFieldValue("api_key", filterUser.api_key || "");
    } else {
      formik.setFieldValue("forward_url", "");
      formik.setFieldValue("forward_url2", "");
    }
  }, [currentUser]);

  useEffect(() => {
    if (formData) {
      call({
        url: `user/${currentUser.id}`,
        data: { ...formData },
      });
    }
  }, [formData]);

  useEffect(() => {
    formik.setSubmitting(false);
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "User Updated" });
      nav(`/group`);
    }
  }, [response]);

  useEffect(() => {
    userCall({ params: { page_size: 1000 } });
    user.admin && groupCall({ params: { page_size: 1000 } });
  }, []);

  useEffect(() => {
    if (userResponse && userResponse.status) {
      setUsers(userResponse.data.data);
    }
  }, [userResponse]);

  useEffect(() => {
    if (groupResponse && groupResponse.status) {
      setGroups(groupResponse.data.data);
      groupResponse.data.data.length === 1 &&
        setCurrentGroup(groupResponse.data.data[0]);
    }
  }, [groupResponse]);

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        if (key === "password" && !values[key]) {
          return obj;
        }
        if (
          (key === "allow_reset" || key === "sip_enabled") &&
          !values[key]
        ) {
          obj[key] = false;
          return obj;
        }
        obj[key] = values[key] || "";
        return obj;
      }, {});

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  const generateAndSetPassword = () => {
    formik.setFieldValue("api_key", generatePassword());
  };

  if (loading || userApiLoading || groupApiLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%", mb: 5 }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title variant="h5" content="Update User" sx={{ mb: 3 }} gutterBottom />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
          maxWidth={350}
        >
          {user.id === 1 && (
            <Grid item xs={12} minWidth={"100%"}>
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...groups.map((group) => ({
                    ...group,
                    label: `${group.group_name}`,
                  })),
                ]}
                name="group"
                label="Group"
                id="group"
                disabled={groups.length === 1}
                value={currentGroup?.group_name}
                isOptionEqualToValue={(option, value) =>
                  option && value && option.id === value.id
                }
                onChange={(e, value) => {
                  setCurrentGroup(value);
                  setCurrentUser(null);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={
                formik.touched.user && Boolean(formik.errors.user)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...users.reduce((acc, subUser) => {
                    if (user.admin && subUser.group_id !== currentGroup?.id) {
                      return acc;
                    }
                    const val = {
                      ...subUser,
                      label: `${subUser.username}${
                        subUser.admin ? "*" : subUser.sub_user ? "^" : ""
                      }`,
                      group_id: subUser.group_id,
                    };
                    acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                name="user"
                label="User"
                id="user"
                onChange={(e, value) => setCurrentUser(value)}
              />
              {formik.touched.user && Boolean(formik.errors.user) ? (
                <FormHelperText id="user-error-text">
                  {formik.errors.user}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={Boolean(formik.errors.password) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="password">Password</InputLabel>
              <OutlinedInput
                name="password"
                label="Password"
                id="password"
                handleBlur={formik.handleBlur}
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              {Boolean(formik.errors.password) ? (
                <FormHelperText id="password-error-text">
                  {formik.errors.password}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          {can_update_user_api(user) && (
            <Grid item xs={12} minWidth={"100%"}>
              <FormControl
                error={Boolean(formik.errors.api_key) ? true : false}
                sx={{ width: "100%" }}
              >
                <InputLabel htmlFor="api_key">Api Key</InputLabel>
                <OutlinedInput
                  name="api_key"
                  label="Api Key"
                  id="api_key"
                  value={formik.values.api_key || ""}
                  onChange={formik.handleChange}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={generateAndSetPassword}
                >
                  Generate Api Key
                  <LockIcon />
                </Button>
                {Boolean(formik.errors.api_key) && (
                  <FormHelperText id="api_key-error-text">
                    {formik.errors.api_key}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} minWidth={"100%"}>
            <InputFieldBox
              name="allow_reset"
              label="Allow Reset"
              id="allow_reset"
              isCheckbox={true}
              value={formik.values.allow_reset}
              onChange={(e) =>
                formik.setFieldValue("allow_reset", e.target.checked)
              }
            />
          </Grid>

          {user && user.id === 1 && (
            <Grid item xs={12} minWidth={"100%"}>
              <InputFieldBox
                name="sip_enabled"
                label="SIP Enabled"
                id="sip_enabled"
                isCheckbox={true}
                value={formik.values.sip_enabled}
                onChange={(e) =>
                  formik.setFieldValue("sip_enabled", e.target.checked)
                }
              />
            </Grid>
          )}

          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={Boolean(formik.errors.forward_url) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="forward_url">Forward URL</InputLabel>
              <OutlinedInput
                name="forward_url"
                label="Forward URL"
                id="forward_url"
                handleBlur={formik.handleBlur}
                value={formik.values.forward_url}
                onChange={formik.handleChange}
                defaultValue={""}
              />
              {Boolean(formik.errors.forward_url) ? (
                <FormHelperText id="forward_url-error-text">
                  {formik.errors.forward_url}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={Boolean(formik.errors.forward_url2) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="forward_url2">Secondary Forward URL</InputLabel>
              <OutlinedInput
                name="forward_url2"
                label="Secondary Forward URL"
                id="forward_url2"
                handleBlur={formik.handleBlur}
                value={formik.values.forward_url2}
                onChange={formik.handleChange}
                defaultValue={""}
              />
              {Boolean(formik.errors.forward_url2) ? (
                <FormHelperText id="forward_url2-error-text">
                  {formik.errors.forward_url2}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          {user && user.id === 1 && (
            <Grid item xs={12} minWidth={"100%"}>
              <FormControl
                error={Boolean(formik.errors.britex_customer_id) ? true : false}
                sx={{ width: "100%" }}
              >
                <Autocomplete
                  multiple
                  fullWidth
                  name="britex_customer_id"
                  id="britex_customer_id"
                  options={[]}
                  value={formik.values.britex_customer_id}
                  freeSolo
                  autoSelect
                  onChange={(e, values) =>
                    formik.setFieldValue("britex_customer_id", values)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="britex_customer_id"
                      id="britex_customer_id"
                      label="Brite Customer ID"
                      placeholder="Ids"
                    />
                  )}
                />
                {Boolean(formik.errors.britex_customer_id) ? (
                  <FormHelperText id="britex_customer_id-error-text">
                    Only numbers are allowed
                  </FormHelperText>
                ) : (
                  ""
                )}
              </FormControl>
            </Grid>
          )}

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={Object.keys(formik.errors).length || !currentUser?.id}
                onClick={() =>
                  currentUser?.id &&
                  !loading &&
                  !formik.isSubmitting &&
                  formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                Update
              </LoadingButton>
            </Grid>
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                size="large"
                onClick={() => nav("/group")}
                loading={loading}
                variant="contained"
              >
                Cancel
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default UpdateUser;
